<template>
  <v-card class="billing-plan__card">
    <v-skeleton-loader
      v-if="subscriptionDataLoading || resourcesLoading"
      class="skeleton-loader"
      type="heading, list-item-three-line, table-cell"></v-skeleton-loader>
    <template v-else>
      <div class="d-flex mb-4">
        <div class="d-flex align-center">
          <div class="d-flex flex-column">
            <v-card-title>{{ $t("settings.billing.billing_plan.title") }}</v-card-title>
            <v-card-subtitle>{{ $t("settings.billing.billing_plan.subtitle") }}</v-card-subtitle>
          </div>
        </div>
        <v-card-actions class="ml-auto">
          <v-btn class="upgrade-plan-btn" color="primary" @click="changePlan">
            <v-icon :size="16"> mdi-star-circle-outline </v-icon>
            {{ $t("settings.billing.billing_plan.change_plan") }}
          </v-btn>
        </v-card-actions>
      </div>
      <ul class="billing-plan__details">
        <li>
          <div class="d-flex align-center">
            <span class="billing-plan__details-name"> {{ billingPlanData.planName }} </span>
            <v-chip :color="billingPlanData.chipColor" :text-color="billingPlanData.chipTextColor">
              {{ billingPlanData.chipText }}
            </v-chip>
          </div>
        </li>
        <li>
          {{
            isTrial ? $t("settings.billing.billing_plan.trial_ends") : $t("settings.billing.billing_plan.plan_renews")
          }}
          <span class="billing-plan__details-value">{{ billingPlanData.activeUntil }}</span>
        </li>
        <li>
          {{ $t("settings.billing.billing_plan.teammates") }}
          <span class="billing-plan__details-value">
            {{ organizationResources.teammates }}/{{ billingPlanData.formattedTeammatesLimit }}
          </span>
        </li>
        <li>
          {{ $t("settings.billing.billing_plan.domains") }}
          <span class="billing-plan__details-value">
            {{ organizationResources.domains }}/{{ billingPlanData.formattedDomainsLimit }}
          </span>
        </li>
      </ul>

      <span class="billing-plan__info">
        <span class="billing-plan__price">{{ billingPlanData.formattedAmount }}</span>
        {{ $t("settings.billing.billing_plan.per_month") }}
      </span>
    </template>
  </v-card>
</template>

<script>
  import { format } from "date-fns";
  import isEmpty from "lodash/isEmpty";
  import { mapGetters } from "vuex";
  import { SUBSCRIPTION_STATUS } from "@/constant";
  import { getOrganizationResources } from "@/@core/utils/getOrganizationResources";
  import { formErrorMessage } from "@/utility/formErrorMessage";

  export default {
    name: "BillingPlan",
    data() {
      return {
        billingPlanData: {},
        organizationResources: {},
        resourcesLoading: false,
      };
    },
    computed: {
      ...mapGetters({
        subscription: "billing/getSubscription",
        subscriptionDataLoading: "billing/getSubscriptionDataLoading",
        isTrial: "billing/getIsTrial",
      }),
    },
    watch: {
      subscription: {
        immediate: true,
        async handler(data) {
          if (!data || isEmpty(data)) return;

          this.billingPlanData = {
            ...data,
            ...this.getBillingPlanStatusData(data),
            ...(() => {
              if (data?.currentPeriodEnd) {
                return {
                  activeUntil: format(new Date(data?.currentPeriodEnd), "MM/dd/yyyy"),
                };
              }

              return {};
            })(),
          };

          await this.getOrganizationResources();
        },
      },
    },
    methods: {
      async getOrganizationResources() {
        try {
          this.resourcesLoading = true;
          const data = await getOrganizationResources();
          this.organizationResources = data;
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.resourcesLoading = false;
        }
      },
      getBillingPlanStatusData(plan) {
        let statusText = plan.status;

        switch (plan.status) {
          case SUBSCRIPTION_STATUS.INCOMPLETE:
            statusText = this.$t("settings.billing.billing_plan.status.incomplete");
            break;
          case SUBSCRIPTION_STATUS.INCOMPLETE_EXPIRED:
            statusText = this.$t("settings.billing.billing_plan.status.incomplete_expired");
            break;
          case SUBSCRIPTION_STATUS.TRIALING:
            statusText = this.$t("settings.billing.billing_plan.status.trialing");
            break;
          case SUBSCRIPTION_STATUS.ACTIVE:
            statusText = this.$t("settings.billing.billing_plan.status.active");
            break;
          case SUBSCRIPTION_STATUS.PAST_DUE:
            statusText = this.$t("settings.billing.billing_plan.status.past_due");
            break;
          case SUBSCRIPTION_STATUS.CANCELED:
            statusText = this.$t("settings.billing.billing_plan.status.canceled");
            break;
          case SUBSCRIPTION_STATUS.UNPAID:
            statusText = this.$t("settings.billing.billing_plan.status.unpaid");
            break;
          case SUBSCRIPTION_STATUS.PAUSED:
            statusText = this.$t("settings.billing.billing_plan.status.paused");
            break;
          case SUBSCRIPTION_STATUS.EARLY_ACCESS:
            statusText = this.$t("settings.billing.billing_plan.status.early_access");
            break;
          default:
            statusText = "-";
            break;
        }

        const infoStatuses = [
          SUBSCRIPTION_STATUS.TRIALING,
          SUBSCRIPTION_STATUS.ACTIVE,
          SUBSCRIPTION_STATUS.EARLY_ACCESS,
        ];

        const errorStatuses = [
          SUBSCRIPTION_STATUS.UNPAID,
          SUBSCRIPTION_STATUS.CANCELED,
          SUBSCRIPTION_STATUS.INCOMPLETE,
          SUBSCRIPTION_STATUS.INCOMPLETE_EXPIRED,
        ];

        const warningStatuses = [SUBSCRIPTION_STATUS.PAST_DUE, SUBSCRIPTION_STATUS.PAUSED];

        if (infoStatuses.includes(plan.status)) {
          return {
            chipColor: "info-120",
            chipTextColor: "info",
            chipText: statusText,
          };
        }

        if (errorStatuses.includes(plan.status)) {
          return {
            chipColor: "error-120",
            chipTextColor: "error",
            chipText: statusText,
          };
        }

        if (warningStatuses.includes(plan.status)) {
          return {
            chipColor: "warning-120",
            chipTextColor: "warning",
            chipText: statusText,
          };
        }
      },
      changePlan() {
        this.$router.push({ name: "change-plan" });
      },
    },
  };
</script>

<style scoped lang="scss">
  @import "../../../../@core/preset/preset/typography/index";
  @import "../../../../@core/preset/preset/functions.scss";

  .billing-plan {
    &__price {
      @include heading-4();
      color: get-color("body");
    }
    &__info {
      @include text-body-2();
      color: get-color("subtitle");
    }
    &__card {
      padding: 24px;
      .v-chip {
        height: 24px;
        padding: 4px 12px 4px 12px !important;
      }
      &-label {
        @include text-body-2();
        color: get-color("subtitle");
        margin-bottom: 16px;
      }

      .upgrade-plan-btn {
        padding-left: 17px !important;
        .v-icon {
          margin-right: 8px;
        }
      }
      .cancel-plan-btn {
        padding-left: 16px !important;
      }
    }

    &__details {
      list-style: none;
      padding: 0;
      margin: 0;
      li {
        @include text-body-1();
        color: get-color("body");
        margin-bottom: 16px;
      }
      &-value {
        color: get-color("subtitle");
      }
      &-name {
        @include heading-4();
        color: get-color("headline");
        margin-right: 8px;
      }
    }
  }

  .skeleton-loader {
    &::v-deep {
      .v-skeleton-loader__heading {
        margin-bottom: 20px;
      }
      .v-skeleton-loader__table-cell {
        height: 25px;
      }
      .v-skeleton-loader__list-item-three-line {
        padding-left: 0;
      }
    }
  }
</style>
